import { useEffect } from 'react'
import { setRecordingActive } from '../redux/flows/portal/currentPatient'
import { useAppDispatch } from '../redux/store'

type NativeMessage = {
  source?: string
  event?: string
  isRecording?: boolean
  intended?: boolean
}

/**
 * Bridges native (expo-av) recording state into Redux.
 *
 * The Expo shell in `kiro-service-portal` posts JSON status messages back into
 * the WebView via `injectJavaScript`, which dispatches a synthetic
 * `MessageEvent` on `window`. This hook listens for those events and mirrors
 * the recording state into the `currentPatientPortal.recordingActive` flag so
 * the rest of the app can react regardless of which screen is mounted.
 *
 * It also pings the native side once on mount in case the user reloaded the
 * web app while a session was in progress and we need to re-sync.
 */
export const useNativeRecordingBridge = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return
      let parsed: NativeMessage | null = null
      try {
        parsed = JSON.parse(event.data) as NativeMessage
      } catch {
        return
      }
      if (!parsed || parsed.source !== 'native') return

      if (typeof parsed.isRecording === 'boolean') {
        // For heartbeats, prefer the "intended" flag - it tracks whether the
        // user wants to be recording. A heartbeat with isRecording=false but
        // intended=true means iOS dropped us; the auto-resume logic on the
        // native side will rebuild, so we still surface the doctor's intent
        // to the UI rather than briefly flipping it off.
        const next =
          parsed.event === 'recording-heartbeat' && typeof parsed.intended === 'boolean'
            ? parsed.intended
            : parsed.isRecording
        dispatch(setRecordingActive(next))
      }
    }

    window.addEventListener('message', handleMessage)

    // Ask native for its current recording state on mount.
    try {
      ;(window as any).ReactNativeWebView?.postMessage(JSON.stringify({ action: 'ping' }))
    } catch {
      // not running in the Expo shell - no-op
    }

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [dispatch])
}
